import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { API_BASE_URL, formatEuri } from '../config/constants';
import { useAuth } from '../context/AuthContext';

// Pomoćna funkcija za današnji lokalni datum (YYYY-MM-DD)
const getTodayString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Pomoćna funkcija za lijepi ispis vremena (npr. "danas u 10:45", "prije 2 dana")
const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '';
  const now = new Date();
  const date = new Date(dateInput);
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const timeStr = date.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `danas u ${timeStr}`;
  if (diffDays === 1) return `jučer u ${timeStr}`;
  return date.toLocaleDateString('hr-HR');
};

export default function Dashboard({ selectedProjectID }) {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const companyID = userInfo?.companyID || 1;

  const [customersCount, setCustomersCount] = useState(0);
  const [todayMeetings, setTodayMeetings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Financijske vrijednosti izračunate iz baze
  const [financials, setFinancials] = useState({
    gdv: 0,
    reservedCapital: 0,
    soldCapital: 0,
    unsoldCapital: 0
  });

  // Brojači statusa stanova
  const [stats, setStats] = useState({ 
    free: 0, 
    reserved: 0, 
    sold: 0,
    total: 0 
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    setLoading(true);

    // 1. Dohvat stanova i izračun vrijednosti
    let unitsUrl = `${API_BASE_URL}/unitList`;
    if (selectedProjectID && selectedProjectID !== 'Sve') {
      unitsUrl += `?projectID=${selectedProjectID}`;
    }

    const fetchUnits = fetch(unitsUrl, { headers })
      .then(res => res.json().catch(() => []))
      .then(data => {
        const unitsList = Array.isArray(data) ? data : [];

        let totalGDV = 0;
        let totalReserved = 0;
        let totalSold = 0;
        let totalFree = 0;

        let freeCount = 0;
        let reservedCount = 0;
        let soldCount = 0;

        unitsList.forEach(u => {
          const directPrice = parseFloat(u.price) || 0;
          const calcPrice = (parseFloat(u.area || u.totalFloorArea || 0) * parseFloat(u.pricePerSqM || u.price_per_sqm || 0)) || 0;
          const unitPrice = directPrice > 0 ? directPrice : calcPrice;

          totalGDV += unitPrice;

          const st = (u.status || '').toLowerCase();
          if (st.includes('rezerv')) {
            totalReserved += unitPrice;
            reservedCount++;
          } else if (st.includes('prodan') || st.includes('prodato')) {
            totalSold += unitPrice;
            soldCount++;
          } else {
            totalFree += unitPrice;
            freeCount++;
          }
        });

        setStats({
          free: freeCount,
          reserved: reservedCount,
          sold: soldCount,
          total: unitsList.length
        });

        setFinancials({
          gdv: totalGDV,
          reservedCapital: totalReserved,
          soldCapital: totalSold,
          unsoldCapital: totalFree
        });
      })
      .catch(err => console.error("Greška stanovi:", err));

    // 2. Dohvat broja kupaca
    const fetchCustomers = fetch(`${API_BASE_URL}/customerList?companyID=${companyID}`, { headers })
      .then(res => res.json().catch(() => []))
      .then(data => {
        setCustomersCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(err => console.error("Greška kupci:", err));

    // 3. Dohvat današnjih sastanaka
    const todayStr = getTodayString();
    let meetingsUrl = `${API_BASE_URL}/meetingList`;
    if (selectedProjectID && selectedProjectID !== 'Sve') {
      meetingsUrl += `?projectID=${selectedProjectID}`;
    }

    const fetchMeetings = fetch(meetingsUrl, { headers })
      .then(res => res.json().catch(() => []))
      .then(data => {
        if (Array.isArray(data)) {
          const todays = data.filter(m => m.meetingDate && String(m.meetingDate).substring(0, 10) === todayStr);
          setTodayMeetings(todays);
        }
      })
      .catch(err => console.error("Greška sastanci:", err));

    // 4. Dohvat aktivnosti
    const fetchActivities = fetch(`${API_BASE_URL}/activityList?companyID=${companyID}`, { headers })
      .then(res => res.json().catch(() => []))
      .then(data => {
        if (Array.isArray(data)) {
          const sorted = data.sort((a, b) => (b.activityID || b.id) - (a.activityID || a.id)).slice(0, 4);
          setActivities(sorted);
        }
      })
      .catch(err => console.error("Greška aktivnosti:", err));

    Promise.all([fetchUnits, fetchCustomers, fetchMeetings, fetchActivities]).finally(() => {
      setLoading(false);
    });
  }, [selectedProjectID, companyID]);

  // Podaci za grafikon (samo statusi koji imaju barem 1 stan)
  const statusChartData = [
    { name: 'Slobodno', value: stats.free, color: '#10b981' },
    { name: 'Rezervirano', value: stats.reserved, color: '#f59e0b' },
    { name: 'Prodano', value: stats.sold, color: '#ef4444' },
  ].filter(item => item.value > 0);

  const soldPercent = financials.gdv > 0 ? Math.round((financials.soldCapital / financials.gdv) * 100) : 0;

  return (
    <div className="space-y-6 max-w-7xl pb-12">
      
      {/* 1. VRH: ČISTI NASLOV */}
      <div>
        <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Nadzorna ploča</h1>
      </div>

      {/* 2. GORNJI RED: 4 JEDNAKE KPI KARTICE */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Vrijednost projekta */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Vrijednost projekta</span>
          <p className="text-2xl font-extrabold text-slate-900 leading-none">{formatEuri(financials.gdv)}</p>
          <p className="text-xs text-slate-400 font-medium">Ukupno: <strong className="text-slate-700">{stats.total} jedinica</strong></p>
        </div>

        {/* Prodano */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Prodano / Ugovoreno</span>
          <p className="text-2xl font-extrabold text-emerald-600 leading-none">{formatEuri(financials.soldCapital)}</p>
          <p className="text-xs text-emerald-600 font-bold">{soldPercent}% realizirano</p>
        </div>

        {/* Rezervirano */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rezervirano</span>
          <p className="text-2xl font-extrabold text-amber-500 leading-none">{formatEuri(financials.reservedCapital)}</p>
          <p className="text-xs text-slate-400 font-medium">Rezervirano: <strong className="text-slate-700">{stats.reserved} stanova</strong></p>
        </div>

        {/* Baza kupaca */}
        <div 
          onClick={() => navigate('/crm')}
          className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24 hover:border-slate-300 transition cursor-pointer group"
        >
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-slate-900 transition">Baza kupaca</span>
          <p className="text-2xl font-extrabold text-slate-900 leading-none">{customersCount}</p>
          <p className="text-xs text-indigo-600 font-bold">Otvori CRM →</p>
        </div>

      </div>

      {/* 3. GLAVNI DIO (STATUSI LIJEVO, SASTANCI I AKTIVNOSTI DESNO) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* LIJEVI STUPAC: STATUS NEKRETNINA */}
        <div className="lg:col-span-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900">Status nekretnina</h2>
            <span className="text-xs font-bold text-slate-400">{stats.total} stanova</span>
          </div>

          {/* Krafna grafikon */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 py-1">
            <div className="h-36 w-36 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData.length > 0 ? statusChartData : [{ name: 'Nema', value: 1, color: '#f1f5f9' }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={64}
                    paddingAngle={statusChartData.length > 1 ? 4 : 0}
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-lg font-extrabold text-slate-900">{soldPercent}%</span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Prodano</span>
              </div>
            </div>

            {/* Legenda statusa */}
            <div className="space-y-2.5 text-xs font-semibold text-slate-600 min-w-[130px]">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Slobodno
                </span>
                <strong className="text-slate-900">{stats.free}</strong>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span> Rezervirano
                </span>
                <strong className="text-slate-900">{stats.reserved}</strong>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span> Prodano
                </span>
                <strong className="text-slate-900">{stats.sold}</strong>
              </div>
            </div>
          </div>

          {/* Progres traka */}
          <div className="pt-3 border-t border-slate-100 space-y-2">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-slate-500">Slobodno za prodaju:</span>
              <span className="text-slate-900">{formatEuri(financials.unsoldCapital)}</span>
            </div>
            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden flex">
              <div style={{ width: `${soldPercent}%` }} className="bg-emerald-500 h-full transition-all duration-500"></div>
            </div>
          </div>
        </div>

        {/* DESNI STUPAC: SASTANCI I AKTIVNOSTI */}
        <div className="lg:col-span-6 space-y-6 flex flex-col justify-between">
          
          {/* Današnji sastanci */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h2 className="text-sm font-bold text-slate-900">Današnji sastanci</h2>
              <button
                type="button"
                onClick={() => navigate('/kalendar')}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 transition cursor-pointer"
              >
                Kalendar →
              </button>
            </div>

            {todayMeetings.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-1">Nema zakazanih sastanaka za danas.</p>
            ) : (
              <div className="space-y-2">
                {todayMeetings.map((m) => (
                  <div 
                    key={m.meetingID}
                    onClick={() => navigate('/kalendar')}
                    className="p-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-100 flex items-center justify-between gap-3 transition cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="bg-slate-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                          {m.meetingTime}
                        </span>
                        <p className="text-xs font-bold text-slate-800 truncate">{m.title}</p>
                      </div>
                      {(m.clientName || m.customerFullName) && (
                        <p className="text-[11px] text-slate-500 truncate mt-0.5">
                          {m.clientName || m.customerFullName}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600 shrink-0">
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Nedavne aktivnosti */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h2 className="text-sm font-bold text-slate-900">Nedavne aktivnosti</h2>
              <button
                type="button"
                onClick={() => navigate('/aktivnosti')}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 transition cursor-pointer"
              >
                Sve →
              </button>
            </div>

            {activities.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-1">Nema zabilježenih aktivnosti.</p>
            ) : (
              <div className="space-y-2">
                {activities.map((act) => {
                  const actDate = act.createdAT || act.createdAt || act.createdat;
                  return (
                    <div 
                      key={act.activityID || act.id} 
                      className="p-2.5 bg-slate-50 rounded-xl text-xs flex items-center justify-between gap-2"
                    >
                      <p className="text-slate-800 font-medium truncate flex-1">{act.body}</p>
                      <span className="text-[10px] text-slate-400 font-bold shrink-0">
                        {formatRelativeTime(actDate)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>

      </div>

    </div>
  );
}